/**
 * Sign in (OutlierOS /auth/login replica). Google is the only sign-in (SPEC §21 decision 20). During the beta
 * (decision 106) a new account needs an invite code: from `?invite=` (remembered) or typed under "Have an invite code?";
 * a valid one rides on the Google link and the API claims it only if this creates an account.
 */
import { useState } from 'react';
import { rememberSignInConsent } from '../components/Privacy';
import { firstTouchParam } from '../analytics';
import { BRAND } from '@vw/core/src/brand';
import { Input } from '@vw/ui';
import { AuthShell, GoogleButton, websiteLink } from '../components/AuthShell';
import { cleanCode } from '../beta';
import { useLocation } from '../router';

const ERRORS: Record<string, string> = {
  cancelled: 'Google sign-in was cancelled. Please try again.',
  expired: 'That sign-in link expired. Please try again.',
  invalid: "Sign-in didn't complete. Please try again.",
  banned: `This account is suspended. If you think this is a mistake, contact ${BRAND.supportEmail}.`,
  signup_limit: 'Too many new accounts were created from this network today. Try again tomorrow.',
  not_admin: 'This account is not an admin.',
  conflict: 'This email is already linked to a different Google account. Sign in with that Google account.',
  invite_required: `${BRAND.name} is in private beta. You need an invite to create an account.`,
};

const REF_KEY = 'vw.ref';
const INVITE_KEY = 'vw.invite';

/** The invite code from ?ref, remembered with the click time so it still counts if sign-up happens later. */
function rememberedRef(fromUrl: string | null): { code: string; at: number } | null {
  try {
    if (fromUrl && /^[A-Za-z0-9]{6,12}$/.test(fromUrl)) {
      const v = { code: fromUrl.toUpperCase(), at: Date.now() };
      localStorage.setItem(REF_KEY, JSON.stringify(v));
      return v;
    }
    const v = JSON.parse(localStorage.getItem(REF_KEY) ?? 'null') as { code?: unknown; at?: unknown } | null;
    return v && typeof v.code === 'string' && typeof v.at === 'number' ? { code: v.code, at: v.at } : null;
  } catch {
    return null;
  }
}

/** The beta invite code from ?invite (remembered, so a detour before signing in keeps it), else the remembered one. */
function rememberedInvite(fromUrl: string | null): string {
  const code = cleanCode(fromUrl);
  try {
    if (code) localStorage.setItem(INVITE_KEY, code);
    return code ?? cleanCode(localStorage.getItem(INVITE_KEY)) ?? '';
  } catch {
    return code ?? '';
  }
}
function rememberInvite(code: string | null) {
  try { if (code) localStorage.setItem(INVITE_KEY, code); else localStorage.removeItem(INVITE_KEY); } catch { /* storage blocked */ }
}
/** Signed in: the invite has done its job (a later sign-up in this browser must not reuse it). */
export const forgetInvite = () => rememberInvite(null);

export function SignIn() {
  const { query } = useLocation();
  const err = query.get('error');
  const raw = query.get('return_to') ?? '/';
  const returnTo = raw.startsWith('/') && !raw.startsWith('//') && !raw.startsWith('/signin') ? raw : '/';
  const ref = query.get('ref');
  const remembered = rememberedRef(ref);
  const refParams = remembered ? `&ref=${encodeURIComponent(remembered.code)}&ref_at=${remembered.at}` : '';
  const [invite, setInvite] = useState(() => rememberedInvite(query.get('invite')));
  // Back with invite_required: the remembered code is the one that was refused.
  const [refused] = useState(() => (err === 'invite_required' ? cleanCode(invite) : null));
  const [inviteOpen, setInviteOpen] = useState(() => !!invite || err === 'invite_required');
  const inviteCode = cleanCode(invite);
  const inviteParam = inviteCode ? `&invite=${inviteCode}` : '';
  return (
    <AuthShell>
      <h1 className="text-2xl font-extrabold tracking-tight">Sign in to {BRAND.name}</h1>
      <p className="mt-2 text-sm text-muted-foreground">Use your Google account. New here? This creates your free account.</p>
      {ref && <div className="mt-4 rounded-xl border border-primary/20 bg-primary/5 p-3 text-sm"><b>You were invited.</b> Sign up free and start finding top Wise Score reels.</div>}
      {err && (
        <div role="alert" className="mt-4 rounded-xl border border-rose-500/20 bg-rose-500/10 p-3 text-sm text-rose-600 dark:text-rose-400">
          {ERRORS[err] ?? ERRORS.invalid}
          {err === 'invite_required' && <> <a className="font-semibold underline" href="/waitlist">Join the waitlist</a></>}
        </div>
      )}
      {inviteOpen ? (
        <label className="mt-6 block text-sm font-semibold">Invite code
          <Input className="mt-1.5 font-mono tracking-widest" value={invite} maxLength={12} autoComplete="off" spellCheck={false} autoCapitalize="characters"
            placeholder="ABCD2345" aria-describedby="invite-help"
            onChange={(e) => { const v = e.target.value.toUpperCase(); setInvite(v); rememberInvite(cleanCode(v)); }} />
          <span id="invite-help" className="mt-1 block text-xs font-normal text-muted-foreground">
            {invite.trim() && !inviteCode ? 'Invite codes are 8 letters and numbers.'
              : refused && inviteCode === refused ? "This code didn't work: it may be used up or expired."
              : 'Only needed for a new account.'}
          </span>
        </label>
      ) : (
        <button type="button" className="mt-4 text-xs font-semibold text-primary hover:underline" onClick={() => setInviteOpen(true)}>Have an invite code?</button>
      )}
      {/* DPDP (decision 44): consent by a clear affirmative action. The statement sits right above the button, which
          says "Agree and continue"; clicking it is the agreement (recorded by the app after sign-in; a link can't fake it). */}
      <div className="mt-6" onClickCapture={() => rememberSignInConsent()}>
        <GoogleButton label="Agree and continue with Google" href={`/api/auth/google/start?return_to=${encodeURIComponent(returnTo)}${refParams}${inviteParam}${firstTouchParam()}`} />
      </div>
      <p className="mt-3 text-xs text-muted-foreground">
        By continuing, you confirm you are 18 or older, you agree to the <a className="font-semibold text-primary underline" href={websiteLink('/terms')} target="_blank" rel="noreferrer">Terms</a>, and you agree that {BRAND.name} uses your Google name, email and picture and what you save and do in the app to run your account, as described in the <a className="font-semibold text-primary underline" href="/privacy-notice" target="_blank" rel="noreferrer">privacy notice</a> (what we keep, for how long, and your rights, including withdrawing at any time).
      </p>
    </AuthShell>
  );
}
